import { memo, useCallback, useMemo } from "react";

import { Text } from "@react/components/Text";
import { VariantSelector } from "@react/components/VariantSelector/VariantSelector.data";
import { findSelectedOption, focusRing } from "@react/utilities";
import {
  type CartProductVariant,
  type CartRenderVariantOptions,
} from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

type Props = {
  productName?: string;
  selected: CartProductVariant | undefined;
  setSelected: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  size?: "small" | "large";
  variants: CartProductVariant[];
};

const styles = {
  label: ({
    active,
    checked,
    size,
  }: {
    active: boolean;
    checked: boolean;
    size: Props["size"];
  }) =>
    tw(
      "flex",
      "flex-1",
      "items-center",
      "justify-center",
      "rounded",
      "border",
      "border-solid",
      "border-interactive-char",
      "bg-char-50",
      "h-[42px]" as TTailwindString,
      {
        "px-8": size === "small",
        "px-9": size === "large",
      },
      "text-primary",
      "no-underline",
      "transition-colors",
      "hover:bg-interactive-char",
      "hover:text-white",
      focusRing,
      {
        "bg-interactive-char": active || checked,
        "text-white": active || checked,
      }
    ),
};

const StyleVariantSelectorBase = ({
  productName,
  selected,
  setSelected,
  showProductName,
  size = "small",
  variants,
}: Props) => {
  const renderOption = useCallback(
    ({ active, checked, currentOption }: CartRenderVariantOptions) => (
      <div
        className={styles.label({
          active,
          checked,
          size,
        })}
      >
        {currentOption && <Text as="body2">{currentOption.value}</Text>}
      </div>
    ),
    [size]
  );

  const styleSelectedOption = useMemo(
    () =>
      selected
        ? findSelectedOption({ type: "style", variant: selected })
        : false,
    [selected]
  );

  return styleSelectedOption ? (
    <VariantSelector
      productName={productName}
      renderOption={renderOption}
      selected={selected}
      selectedOption={styleSelectedOption}
      setSelected={setSelected}
      showProductName={showProductName}
      type="style"
      variants={variants}
    />
  ) : null;
};

export const StyleVariantSelector = memo(StyleVariantSelectorBase);
